import {
	CheckOutlined,
	CloseOutlined,
	ExclamationCircleOutlined,
	MoneyCollectOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { midMonthPayApi } from 'Api/midMonthPays';
import { Button, Divider, Modal, Table, Tag, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import Title from 'antd/es/typography/Title';
import Buttons from 'components/Global/Buttons';
import Ellipse from 'components/Global/Ellipse';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { bankMapping } from 'utils/mappings';
import {
	Paid,
	PaymentUnderProcessWithText,
	PendingForApproval,
	RejectedByAccounts,
} from 'utils/svgs';
import { decrypt, formatNumber, invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
type Props = {
	isOpen: boolean;
	handleCancel: () => void;
	payGroupId: string;
	selectedBankId: string;
	currency: string;
	selectedBankData: any;
	fetchMidMonthData: any;
};

const encryptionKey: any = process.env.REACT_APP_ENCRYPTION_KEY;
const AddPreviewModal = (props: Props) => {
	const {
		isOpen,
		handleCancel,
		payGroupId,
		selectedBankId,
		currency,
		selectedBankData,
		fetchMidMonthData,
	} = props;

	const { data } = useSelector((state: any) => state?.userProfile);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
	const [employeeData, setEmployeeData] = useState<any[]>([]);

	const handleProceedToPayment = async () => {
		try {
			setIsButtonLoading(true);

			if (!employeeData || employeeData.length === 0) {
				toastText('No employee data found', 'error');
				return;
			}
			let response;

			if (payGroupId) {
				const initiatePayload = {
					midMonthPayGroupId: payGroupId,
					companyBankId: selectedBankId,
				};
				response =
					selectedBankData?.bankType === 'STANBIC_BANK'
						? await midMonthPayApi.proceedToMidMonthPaymentForStanbic(
								initiatePayload
						  )
						: await midMonthPayApi.proceedToMidMonthPaymentForFCB(
								initiatePayload
						  );

				await fetchEmployeeList(payGroupId);
				fetchMidMonthData();
				toastText(
					response.data.message ||
						'Bank transfer initiated successfully',
					'success'
				);
			} else {
				toastText(
					'Something went wrong in getting midMonthId',
					'error'
				);
			}
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in bank transfer';
			toastText(errorMessage, 'error');
		} finally {
			setIsButtonLoading(false);
		}
	};

	const fetchEmployeeList = async (id: string) => {
		try {
			setIsLoading(true);
			const response = await midMonthPayApi.getEmployeeDetailsForMidMonth(
				id
			);
			const employeeArray = response.data?.employeeDetails;

			if (Array.isArray(employeeArray) && employeeArray.length > 0) {
				setEmployeeData(employeeArray);
			} else if (
				employeeArray &&
				typeof employeeArray === 'object' &&
				!Array.isArray(employeeArray)
			) {
				setEmployeeData([employeeArray]);
			} else {
				setEmployeeData([]);
				toastText('No employee data found', 'error');
			}
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching employee details';
			toastText(errorMessage, 'error');
			setEmployeeData([]);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (payGroupId && isOpen) {
			fetchEmployeeList(payGroupId);
		}

		return () => {
			// Cleanup when modal closes
			if (!isOpen) {
				setEmployeeData([]);
			}
		};
	}, [payGroupId, isOpen]);

	const hasProcessedPayments = useMemo(() => {
		if (!Array.isArray(employeeData) || employeeData.length === 0) {
			return false;
		}
		return employeeData.every(
			(emp) => emp?.status === 'SUCCESS' || emp?.status === 'PROCESSING'
		);
	}, [employeeData]);

	const shouldDisableProceedButton = useMemo(() => {
		const result =
			isLoading ||
			!Array.isArray(employeeData) ||
			employeeData.length === 0 ||
			hasProcessedPayments;

		return result;
	}, [isLoading, employeeData, hasProcessedPayments]);

	const myButtons = [
		{
			text: 'Back',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isLoading,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			onclick: () => {
				handleCancel();
			},
		},
		...(shouldDisableProceedButton
			? []
			: [
					{
						text: 'Proceed to Payment',
						isLoading: isButtonLoading,
						className: 'btn-blue',
						isSubmit: false,
						fontSize: '1.8rem',
						minWidth: '12rem',
						minHeight: '4rem',
						disabled: isLoading,
						onclick: () => {
							handleProceedToPayment();
						},
						font: '16px',
					},
			  ]),
	];

	const employeePlural =
		employeeData && employeeData.length > 1 ? 'employees' : 'employee';
	const employeeCountText =
		employeeData && employeeData.length > 0
			? `${employeeData.length} ${employeePlural}`
			: 'employees';

	return (
		<Modal
			open={isOpen}
			closable={false}
			onCancel={handleCancel}
			centered
			rootClassName="addPaymentModalRoot"
			style={{
				minWidth: '1300px',
			}}
			footer={false}
		>
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<Title level={4}>
						<span>
							Review the payment details for{' '}
							<span style={{ color: '#584495' }}>
								{employeeCountText}
							</span>{' '}
							and click Proceed.
						</span>
					</Title>
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'center',
							gap: '20px',
						}}
					>
						<Tooltip title="Refresh employee list">
							<SyncOutlined
								style={{
									fontSize: '20px',
									cursor: isLoading
										? 'not-allowed'
										: 'pointer',
									color: '#584495',
									transition: 'transform 0.3s',
									marginRight: '10px',
								}}
								onClick={
									!isLoading
										? () => fetchEmployeeList(payGroupId)
										: undefined
								}
							/>
						</Tooltip>

						<div>
							<Button
								icon={<CloseOutlined />}
								onClick={handleCancel}
							/>
						</div>
					</div>
				</div>
				<Divider
					style={{
						margin: '12px 0 ',
						padding: '0',
					}}
				/>
				<div className={styles['company-account-details']}>
					<p className={styles['details-label']}>
						Company Account Details
					</p>
					<br />
				</div>
				<div className={styles['company-account-details']}>
					<div className={styles['detail-group']}>
						<div className={styles['detail-label']}>
							Account Name
						</div>
						<div className={styles['detail-value']}>
							{selectedBankData?.accountName}
						</div>
					</div>

					<div className={styles['detail-group']}>
						<div className={styles['detail-label']}>Bank Type</div>
						<div className={styles['detail-value']}>
							{bankMapping[selectedBankData?.bankType]?.name}
						</div>
					</div>

					{selectedBankData?.accountNumber && (
						<div className={styles['detail-group']}>
							<div className={styles['detail-label']}>
								Account Number
							</div>
							<div className={styles['detail-value']}>
								{decrypt(
									selectedBankData?.accountNumber,
									encryptionKey
								)}
							</div>
						</div>
					)}
				</div>
				<div
					className={styles['modal-body']}
					style={{ marginBottom: 0 }}
				>
					<div className="payment-table">
						<Table
							dataSource={employeeData || []}
							scroll={{
								x: 1500,
								y: 'calc(100vh - 370px)',
							}}
							rowKey={(record: any) => record.id}
							pagination={false}
							style={{
								marginTop: 2,
							}}
							loading={isLoading}
						>
							<Column
								title="Employee Name"
								dataIndex="employeeName"
								key="employeeName"
								className="bg-white"
								width={130}
								fixed="left"
								render={(text: string, record: any) => {
									try {
										const {
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
											employeeCode,
										} = record || {};

										const address = [
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
										]
											.filter(Boolean)
											.join(' ');

										const hasSpecialChar =
											address &&
											/[^a-zA-Z0-9\s]/.test(address);

										return (
											<span>
												{text || '-'}
												{employeeCode && (
													<span
														style={{
															color: '#584495',
															fontWeight: 500,
														}}
													>
														{' '}
														| {employeeCode}
													</span>
												)}
												{hasSpecialChar && (
													<Tooltip title="Address contains special characters">
														<ExclamationCircleOutlined
															style={{
																color: '#faad14',
																marginLeft: 6,
															}}
														/>
													</Tooltip>
												)}
											</span>
										);
									} catch (error) {
										return <span>{text || '-'}</span>;
									}
								}}
							/>

							<Column
								title="Bank"
								dataIndex="bank"
								key="bank"
								className="bg-white"
								width={80}
								render={(value: string) => {
									return bankMapping[value]?.name ?? '';
								}}
							/>
							<Column
								title="Bank Account Number"
								key="accountDetails"
								className="bg-white"
								width={150}
								render={(_, record: any) => {
									try {
										// More defensive check
										const accountNumber =
											record?.accountNumber &&
											typeof record.accountNumber ===
												'string' &&
											record.accountNumber.trim() !==
												'' &&
											!invalidText(record.accountNumber)
												? decrypt(
														record.accountNumber,
														encryptionKey
												  )
												: '';

										if (!accountNumber) {
											return <span>-</span>;
										}

										let isVerified = false;
										if (
											record.bank === 'FIRST_CAPITAL_BANK'
										) {
											isVerified =
												record?.accountVerified;
										} else {
											isVerified = true;
										}

										return (
											<Tooltip
												title={
													isVerified
														? 'Account number is verified'
														: 'Account number is not verified'
												}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: 8,
													}}
												>
													<span>{accountNumber}</span>
													{isVerified ? (
														<CheckOutlined
															style={{
																color: 'green',
															}}
														/>
													) : (
														<CloseOutlined
															style={{
																color: 'red',
															}}
														/>
													)}
												</div>
											</Tooltip>
										);
									} catch (error) {
										return <span>-</span>;
									}
								}}
							/>
							<Column
								title="Sort Code"
								dataIndex="sortCode"
								key="sortCode"
								className="bg-white"
								width={75}
							/>
							<Column
								title="Amount"
								dataIndex="amount"
								key="amount"
								className="bg-white"
								width={80}
								render={(value: string) => {
									return formatNumber(
										Number(value),
										data?.CompanyCurrencies,
										currency === 'USD'
									);
								}}
							/>
							<Column
								title="Beneficiary Address"
								key="beneficiaryAddress"
								width={'15%'}
								className="bg-white"
								render={(_, record) => {
									try {
										const {
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
										}: any = record || {};

										const addressParts = [
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
										].filter(Boolean);

										const address =
											addressParts.length > 0
												? addressParts.join(' ')
												: '-';

										return (
											<Ellipse
												message={address}
												maxLength={50}
												tooltipMessage={address}
												isTooltip={true}
											/>
										);
									} catch (error) {
										return <span>-</span>;
									}
								}}
							/>
							<Column
								title="Status"
								dataIndex="status"
								key="status"
								width={100}
								className={styles['statusColumn'] + ' bg-white'}
								render={(value: string, record: any) => {
									let statusText = '';
									let statusClass = '';
									let StatusIcon = null;

									const currentStatus = value;

									switch (currentStatus) {
										case 'PENDING':
											statusText = 'Pending';
											statusClass =
												styles['status-pending'];
											StatusIcon = <PendingForApproval />;
											break;
										case 'FAILED':
											statusText = 'Failed';
											statusClass =
												styles['status-rejected'];
											StatusIcon = <RejectedByAccounts />;
											break;
										case 'SUCCESS':
											statusText = 'Success';
											statusClass =
												styles['status-success'];
											StatusIcon = <Paid />;
											break;
										case 'PROCESSING':
											statusClass =
												styles['status-processing'];
											StatusIcon = (
												<PaymentUnderProcessWithText />
											);

											break;
										case 'DONE_VIA_CASH':
											statusClass =
												styles['status-success'];
											StatusIcon = (
												<MoneyCollectOutlined />
											);

											break;
										default:
											statusText = 'Unknown Status';
											statusClass =
												styles['status-unknown'];
									}

									return (
										<div
											className={
												styles['statusContainer']
											}
										>
											<div
												className={
													styles['statusDisplay']
												}
											>
												<div
													className={
														styles['statusAndEdit']
													}
												>
													<Tag
														className={`${styles['statusTag']} ${statusClass}`}
														bordered={false}
													>
														<span
															className={
																styles[
																	'statusIcon'
																]
															}
														>
															{StatusIcon}
														</span>
														<span>
															{statusText}
														</span>
													</Tag>
												</div>
											</div>
										</div>
									);
								}}
							/>
						</Table>
					</div>
				</div>
			</div>
			<div>
				<Buttons buttons={myButtons} />
			</div>
		</Modal>
	);
};

export default AddPreviewModal;
